import { Check } from "lucide-react";
import React from "react";

export interface Awards {
  verifiedClassCount: number;
  verifiedStudioCount: number;
  categoryCounts: Record<string, number>;
}

interface ProgressSectionProps {
  awards: Awards;
}

const getNextMilestoneMessage = (count: number): string => {
  const thresholds = [5, 10, 20, 50, 100, 250];
  const next = thresholds.find(t => count < t);
  return next ? `Estás a sólo ${next - count} clases de tu siguiente logro.` : '';
};

interface ProgressCircleProps {
  percentage: number;
  label: number;
  isActive: boolean;
  type: 'class' | 'partner';
}

export const ProgressCircle: React.FC<ProgressCircleProps> = ({
  percentage,
  label,
  type,
}) => {
  const size = 125;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - percentage / 100);

  const color = type === 'class' ? '#4e2c7d' : '#37a1f7';
  const ringBg = '#e5e5e5';

  return (
    <div className="relative inline-block">
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        <circle
          className="opacity-50"
          stroke={ringBg}
          strokeWidth={strokeWidth}
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
        />
        <circle
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-sm font-bold text-center leading-none gap-0.5">
        <span>{label}</span>
        <span className="uppercase text-[10px]">
          {type === 'class' ? 'clases' : 'partners'}
        </span>
      </div>
    </div>
  );
};



export const ProgressSection: React.FC<ProgressSectionProps> = ({ awards }) => {
  const categoryAwardsAchieved = Object.keys(awards.categoryCounts).filter(
    key => awards.categoryCounts[key] >= 5
  ).length;

  return (
    <div className="mx-3 mb-25 mt-9 max-w-6xl mx-auto">
      <div className="bg-white rounded border border-gray-200 py-4 shadow-md">
        <div className="text-2xl font-bold px-4 pb-2 border-b border-gray-200">Mi progreso</div>

        <div className="flex text-center text-sm py-3 border-b border-gray-200">
          <div className="w-1/3 border-r border-gray-200">
            <div className="font-bold text-holos-dark text-lg">{awards.verifiedClassCount}</div>
            <div className="uppercase text-md">Clases o sesiones asistidas</div>
          </div>
          <div className="w-1/3 border-r border-gray-200">
            <div className="font-bold text-lg">{awards.verifiedStudioCount}</div>
            <div className="uppercase text-md">Partners visitados</div>
          </div>
          <div className="w-1/3">
            <div className="font-bold text-lg">{categoryAwardsAchieved}</div>
            <div className="uppercase text-md">Actividades hechas</div>
          </div>
        </div>

        <div className="px-4 pt-6 my-5">
          <h4 className="font-medium text-lg font-juana">Clases o sesiones realizadas</h4>
          {awards.verifiedClassCount > 0 && (
            <h6 className="mb-4 mt-2 font-medium">
              ¡{awards.verifiedClassCount} clases completadas! {getNextMilestoneMessage(awards.verifiedClassCount)}
            </h6>
          )}

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-15 justify-items-center">
            {[5, 10, 20, 50, 100, 250].map((threshold, i) => {
              const prev = i === 0 ? 0 : [5, 10, 20, 50, 100, 250][i - 1];
              const progress = Math.max(Math.min(((awards.verifiedClassCount - prev) / (threshold - prev)) * 100, 100), 0);
              const isActive = awards.verifiedClassCount >= threshold;
              return (
                <ProgressCircle key={threshold} percentage={progress} label={threshold} isActive={isActive} type="class" />
              );
            })}
          </div>
        </div>

        <div className="px-4">
          <h4 className="font-medium text-lg font-juana mt-4">Partners visitados</h4>
          {awards.verifiedStudioCount > 0 && (
            <h6 className="mb-4 mt-2 font-medium">
              ¡Vas probando {awards.verifiedStudioCount} partners! Continúa explorando.
            </h6>
          )}

          <div className="flex flex-wrap mb-15 space-x-4">
            {[5, 10, 15, 20, 50, 100].map((threshold, i) => {
              const prev = i === 0 ? 0 : [5, 10, 15, 20, 50, 100][i - 1];
              const progress = Math.max(Math.min(((awards.verifiedStudioCount - prev) / (threshold - prev)) * 100, 100), 0);
              const isActive = awards.verifiedStudioCount >= threshold;
              return (
                <ProgressCircle key={threshold} percentage={progress} label={threshold} isActive={isActive} type="partner" />
              );
            })}
          </div>
        </div>

        <div className="px-4">
          <h4 className="font-medium text-lg font-juana mt-4">Actividades completadas</h4>
          {categoryAwardsAchieved > 0 ? (
            <h6 className="mb-4 mt-2 font-medium">
              Ya vas probando {categoryAwardsAchieved} actividades. Sigue viviendo miles de experiencias.
            </h6>
          ) : (
            <h6 className="mb-4 mt-2 font-medium">Aún no hay actividades hechas.</h6>
          )}

          <div className="flex flex-wrap">
            {Object.keys(awards.categoryCounts).map(category => {
              const count = awards.categoryCounts[category] || 0;
              return count > 0 ? (
                <div key={category} className="flex flex-col items-center mr-4 mt-3 text-center text-xs">
                  <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                    <Check />
                  </div>
                  <span className="truncate w-16 mt-1 text-base">{category}</span>
                  <span className="text-base">{count}</span>
                </div>
              ) : null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
